import { useEffect, useRef } from 'react';
import { getThemedChat, User } from './helpers';

interface UseTalkJSInterface {
  theme: string;
  user: User;
}

const useTalkJS = ({ theme, user }: UseTalkJSInterface) => {
  const shellRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let activeContainer: HTMLDivElement | null = null;

    const alignGhost = () => {
      if (!shellRef.current || !activeContainer) return;
      const rect = shellRef.current.getBoundingClientRect();

      const absoluteTop = rect.top + window.scrollY;
      const absoluteLeft = rect.left + window.scrollX;

      Object.assign(activeContainer.style, {
        top: `${absoluteTop}px`,
        left: `${absoluteLeft}px`,
        width: `${rect.width}px`,
        height: `${rect.height}px`,
        visibility: rect.height > 0 ? 'visible' : 'hidden',
      });
    };

    const setup = async () => {
      const { container } = await getThemedChat({ theme, user });
      activeContainer = container;
      alignGhost();
    };

    setup();

    const resizer = new ResizeObserver(alignGhost);
    if (shellRef.current) resizer.observe(shellRef.current);

    window.addEventListener('scroll', alignGhost, { passive: true });
    window.addEventListener('resize', alignGhost);

    const bodyResizer = new ResizeObserver(alignGhost);
    bodyResizer.observe(document.body);

    return () => {
      resizer.disconnect();
      bodyResizer.disconnect();
      window.removeEventListener('scroll', alignGhost);
      window.removeEventListener('resize', alignGhost);
      if (activeContainer) {
        activeContainer.style.visibility = 'hidden';
        activeContainer.style.pointerEvents = 'none';
        activeContainer.style.top = '-10000px';
      }
    };
  }, [theme, user]);

  return shellRef;
};

export default useTalkJS;
